import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ExportEvidenceSetDocument,
  type ExportEvidenceSetMutation,
  type ExportEvidenceSetMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { downloadFile } from "~/utils/download";

interface UseEvidenceSetExportProps {
  onClose: () => void;
  evidenceSetId: string;
}

export function useEvidenceSetExport({ onClose, evidenceSetId }: UseEvidenceSetExportProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [exporting, setExporting] = useState(false);
  const [exportEvidenceSet] = useMutation<ExportEvidenceSetMutation, ExportEvidenceSetMutationVariables>(
    ExportEvidenceSetDocument,
  );

  const onExport = async () => {
    setExporting(true);
    try {
      if (!tenantId) {
        throw new Error("Tenant is not resolved");
      }

      const { data } = await exportEvidenceSet({
        variables: { input: { tenantId, id: evidenceSetId, format: "json" } },
        fetchPolicy: "no-cache",
      });

      const result = data?.cnutExportEvidenceSet;
      if (result?.__typename === "EvidenceSetExportOutput" && result.url) {
        downloadFile(result.url, `dewey-evidence-set-${evidenceSetId}.json`);
      } else {
        toast.error(t("feature.common.failedToExport"));
      }
    } catch (error) {
      toast.error(translateError(t, error));
    } finally {
      setExporting(false);
      onClose();
    }
  };

  return { onExport, exporting };
}
